import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, FileText, Play, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAppAuth } from "@/auth/use-app-auth";
import { useAuthFlow, getOrgFromLocation } from "@/lib/auth-flow";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { EvidenceShortlistPreview } from "./review-preview";

export const HeroSection = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppAuth();
  const startAuth = useAuthFlow();
  const reducedMotion = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 12]);
  const handleCta = async () => {
    if (!isAuthenticated) {
      await startAuth({ mode: "login", returnTo: "/dashboard", orgAuthId: getOrgFromLocation() });
      return;
    }
    navigate("/dashboard");
  };
  return (
    <section id="hero" className="recruit-hero" ref={ref}>
      <div className="recruit-shell recruit-hero-grid">
        <div className="recruit-hero-copy">
          <p className="recruit-eyebrow">
            <span className="recruit-signal-dot" /> A clearer view of your next hire
          </p>
          <h1>
            Build a shortlist
            <br />
            you can <span>explain.</span>
          </h1>
          <p className="recruit-lead">
            Find relevant candidates. Hear their story. Go deeper with AI screening interviews—and
            inspect the sources behind your next conversation.
          </p>
          <div className="recruit-actions">
            <button className="recruit-button recruit-button-primary" onClick={handleCta}>
              {isAuthenticated ? "Go to dashboard" : "Start with a role"}
              <ArrowRight size={17} />
            </button>
            <a href="#workflow" className="recruit-button recruit-button-secondary">
              See the workflow
              <ArrowDown size={16} />
            </a>
          </div>
          <Dialog>
            <DialogTrigger asChild>
              <button className="recruit-text-link recruit-demo-link">
                <Play size={14} /> Watch the product walkthrough
              </button>
            </DialogTrigger>
            <DialogContent className="recruit-video-dialog">
              <div className="flex items-center justify-between gap-4">
                <DialogTitle>Inside theRecruit</DialogTitle>
                <DialogClose aria-label="Close product walkthrough">
                  <X size={20} />
                </DialogClose>
              </div>
              <DialogDescription>
                Recorded product walkthrough. Interface and availability may vary by plan.
              </DialogDescription>
              <video
                controls
                playsInline
                preload="metadata"
                src="https://d1ukqlfbhdwy5x.cloudfront.net/videos/therecruit-hero.mp4"
                aria-label="theRecruit product walkthrough"
              />
            </DialogContent>
          </Dialog>
        </div>
        <motion.div className="recruit-hero-visual" style={{ y: reducedMotion ? 0 : y }}>
          <div className="recruit-role-slip" aria-hidden="true">
            <FileText size={16} />
            <span>ROLE BRIEF</span>
            <strong>Product Lead</strong>
            <span>B2B SaaS</span>
          </div>
          <EvidenceShortlistPreview />
        </motion.div>
      </div>
      <div className="recruit-shell recruit-hero-footnote">
        <span>A resume is the beginning. Not the whole picture.</span>
        <div>
          <span>Discover</span>
          <span>Understand</span>
          <span>Decide</span>
        </div>
      </div>
    </section>
  );
};
